import React from 'react';
import { createPortal } from 'react-dom';
import { Download, X, FileText } from 'lucide-react';

interface PDFResource {
  title: string;
  url: string;
}

interface PDFSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  pdfs: PDFResource[];
  onDownload: (filenames: string[]) => void;
}

export function PDFSelectionModal({
  isOpen,
  onClose,
  pdfs,
  onDownload
}: PDFSelectionModalProps) {
  const [selectedPDFs, setSelectedPDFs] = React.useState<Set<string>>(new Set());
  const modalRef = React.useRef<HTMLDivElement>(null);
  const closeButtonRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    if (isOpen) {
      setSelectedPDFs(new Set());
      document.body.style.overflow = 'hidden';
      
      // Hide sticky navigation when modal is open (using display:none removes from accessibility tree)
      const navElements = document.querySelectorAll('.section-navigation');
      navElements.forEach((el) => {
        (el as HTMLElement).style.display = 'none';
      });
      
      return () => {
        document.body.style.overflow = '';
        navElements.forEach((el) => {
          (el as HTMLElement).style.display = '';
        });
      };
    }
  }, [isOpen]);

  const handleTogglePDF = (url: string) => {
    const newSelected = new Set(selectedPDFs);
    if (newSelected.has(url)) {
      newSelected.delete(url);
    } else {
      newSelected.add(url);
    }
    setSelectedPDFs(newSelected);
  };

  const handleDownload = () => {
    if (selectedPDFs.size === 0) {
      alert('Please select at least one PDF to download.');
      return;
    }
    onDownload(Array.from(selectedPDFs));
    onClose();
  };

  const handleDownloadBoth = () => {
    const allUrls = pdfs.map(pdf => pdf.url);
    onDownload(allUrls);
    onClose();
  };

  // Focus management for accessibility
  React.useEffect(() => {
    if (isOpen && closeButtonRef.current) {
      closeButtonRef.current.focus();
    }
  }, [isOpen]);

  // Handle Escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div 
      role="dialog"
      aria-modal="true"
      aria-labelledby="pdf-modal-title"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 999999,
        padding: '1rem'
      }}
      onClick={onClose}
    >
      <div 
        ref={modalRef}
        style={{
          background: 'white',
          borderRadius: '12px',
          padding: '2rem',
          maxWidth: '500px',
          width: '100%',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '1.5rem'
        }}>
          <h2 
            id="pdf-modal-title"
            style={{
              margin: 0,
              fontSize: '1.5rem',
              fontWeight: '700',
              color: 'var(--dark-gray)'
            }}
          >
            Select PDFs to Download
          </h2>
          <button
            ref={closeButtonRef}
            onClick={onClose}
            aria-label="Close modal"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--gray)'
            }}
          >
            <X style={{ width: '24px', height: '24px' }} aria-hidden="true" />
          </button>
        </div>

        <p style={{
          margin: '0 0 1.5rem 0',
          fontSize: '0.95rem',
          color: 'var(--gray)',
          lineHeight: '1.6'
        }}>
          Choose which PDFs you'd like to download:
        </p>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          {pdfs.map((pdf, index) => (
            <label
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                padding: '1rem',
                border: selectedPDFs.has(pdf.url) ? '2px solid var(--orange)' : '2px solid #e5e7eb',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                background: selectedPDFs.has(pdf.url) ? 'rgba(216, 183, 37, 0.05)' : 'white'
              }}
              onMouseOver={(e) => {
                if (!selectedPDFs.has(pdf.url)) {
                  e.currentTarget.style.borderColor = 'var(--orange)';
                  e.currentTarget.style.background = 'rgba(216, 183, 37, 0.02)';
                }
              }}
              onMouseOut={(e) => {
                if (!selectedPDFs.has(pdf.url)) {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.background = 'white';
                }
              }}
            >
              <input
                type="checkbox"
                checked={selectedPDFs.has(pdf.url)}
                onChange={() => handleTogglePDF(pdf.url)}
                style={{
                  width: '20px',
                  height: '20px',
                  cursor: 'pointer',
                  accentColor: 'var(--orange)'
                }}
              />
              <FileText style={{ width: '24px', height: '24px', color: 'var(--orange)' }} />
              <span style={{
                flex: 1,
                fontSize: '1rem',
                fontWeight: '600',
                color: 'var(--dark-gray)'
              }}>
                {pdf.title}
              </span>
            </label>
          ))}
        </div>

        <div style={{
          display: 'flex',
          gap: '1rem',
          flexDirection: 'column'
        }}>
          <button
            onClick={handleDownloadBoth}
            style={{
              width: '100%',
              background: 'var(--orange)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '1rem',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = '#e68900';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'var(--orange)';
            }}
          >
            <Download style={{ width: '20px', height: '20px' }} />
            Download Both
          </button>

          <button
            onClick={handleDownload}
            disabled={selectedPDFs.size === 0}
            style={{
              width: '100%',
              background: selectedPDFs.size === 0 ? '#ccc' : 'var(--dark)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '1rem',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: selectedPDFs.size === 0 ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              if (selectedPDFs.size > 0) {
                e.currentTarget.style.background = '#042789';
              }
            }}
            onMouseOut={(e) => {
              if (selectedPDFs.size > 0) {
                e.currentTarget.style.background = 'var(--dark)';
              }
            }}
          >
            <Download style={{ width: '20px', height: '20px' }} />
            Download Selected ({selectedPDFs.size})
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
